/**
 * Branding Schema + System Default — Wave 1c W1c-05.
 *
 * Single source of truth for the TenantBranding shape, the SYSTEM_DEFAULT,
 * and the writable subset.
 *
 * Imported from:
 *  - api: services/branding-resolver.service.ts (resolves effective)
 *  - api: routes/v1/branding.ts (validates PUT body)
 *  - web: lib/branding-defaults.ts (re-exports for compatibility)
 *  - web: contexts/branding-context.tsx (initial state fallback)
 *
 * The shape is the LOGICAL data model. The DB persistence layer (b34
 * columns) maps onto this 1:1 except that asset references go through
 * the `branding_assets` FK chain.
 */

import { z } from 'zod';

// ─── Schemas ────────────────────────────────────────────────────────────

/** Hex color regex — strict 6-digit form (#RRGGBB). */
export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

export const BrandingColorsSchema = z.object({
  primary: z.string().regex(HEX_COLOR_RE),
  primaryForeground: z.string().regex(HEX_COLOR_RE),
  background: z.string().regex(HEX_COLOR_RE),
  card: z.string().regex(HEX_COLOR_RE),
  text: z.string().regex(HEX_COLOR_RE),
  popover: z.string().regex(HEX_COLOR_RE),
  sidebarBackground: z.string().regex(HEX_COLOR_RE),
  sidebarText: z.string().regex(HEX_COLOR_RE),
  sidebarActive: z.string().regex(HEX_COLOR_RE),
  sidebarActiveText: z.string().regex(HEX_COLOR_RE),
  sidebarHover: z.string().regex(HEX_COLOR_RE),
});
export type BrandingColors = z.infer<typeof BrandingColorsSchema>;

export const DisplayModeSchema = z.enum(['full', 'both', 'icon-name']);
export type DisplayMode = z.infer<typeof DisplayModeSchema>;

/**
 * Effective branding shape returned by GET /branding (resolved across the
 * tenant override / platform default / system default chain).
 */
export const TenantBrandingSchema = z.object({
  useDefaultTheme: z.boolean(),
  displayMode: DisplayModeSchema,
  portalTitle: z.string().min(1).max(120),

  // Asset URLs (relative paths that resolve to GET /api/v1/branding/assets/<id>?v=<effective_version>).
  // null = no override; UI uses the system fallback image.
  logoUrl: z.string().nullable(),
  logoIconUrl: z.string().nullable(),
  faviconUrl: z.string().nullable(),
  loginBackgroundUrl: z.string().nullable(),

  // Login page text
  loginWelcomeText: z.string().nullable(),
  loginSubtitle: z.string().nullable(),
  footerText: z.string().nullable(),

  light: BrandingColorsSchema,
  dark: BrandingColorsSchema,

  // Diagnostics + cache control
  effectiveVersion: z.number().int().nonnegative(),
  /** Sysadmin-locked field names. Tenant admin cannot override these. */
  lockedFields: z.array(z.string()),
  /** Resolution layer that produced this row's values. */
  source: z.enum(['tenant', 'platform', 'system_default']),
});
export type TenantBranding = z.infer<typeof TenantBrandingSchema>;

/**
 * Body of PUT /branding — the writable subset of the effective shape.
 * Asset slots accept asset IDs (UUID) instead of URLs; the server resolves
 * them to URLs on read.
 *
 * Locked fields are accepted (omitted means "leave alone"); server rejects
 * any locked field whose value differs from the current locked value.
 */
// Bug #928 — Strict so unknown keys are rejected. Previously a caller
// could POST `{"lightTheme": {...}}` (note the wrong key — the schema
// uses `light`, not `lightTheme`) and Zod would silently strip it,
// returning 200 with the unchanged platform default theme. With
// `.strict()` the wrong-keyed payload now returns 422 with a clear
// "unrecognized key" error and the caller gets actionable feedback.
// Valid payloads under `light` / `dark` still flow through, and the
// existing hex regex (`HEX_COLOR_RE`) catches the `"notahex"` case
// the matrix row ADMIN-BRAND-003 explicitly probes.
export const TenantBrandingWriteSchema = z.object({
  useDefaultTheme: z.boolean().optional(),
  displayMode: DisplayModeSchema.optional(),
  portalTitle: z.string().min(1).max(120).optional(),

  logoFullAssetId: z.string().uuid().nullable().optional(),
  logoIconAssetId: z.string().uuid().nullable().optional(),
  faviconAssetId: z.string().uuid().nullable().optional(),
  loginBackgroundAssetId: z.string().uuid().nullable().optional(),

  loginWelcomeText: z.string().max(200).nullable().optional(),
  loginSubtitle: z.string().max(200).nullable().optional(),
  footerText: z.string().max(200).nullable().optional(),

  light: BrandingColorsSchema.optional(),
  dark: BrandingColorsSchema.optional(),

  /**
   * If set true, the server saves a low-contrast color pair anyway and
   * records the acknowledgment in the audit row. If unset and contrast
   * is below WCAG-AA, the response includes a warning but still saves
   * (per Wave 1c Q5 override — accountability is v1; hard-block is v1.1).
   */
  acknowledgeLowContrast: z.boolean().optional(),
}).strict();
export type TenantBrandingWrite = z.infer<typeof TenantBrandingWriteSchema>;

/**
 * Body of GET /branding/login — public, no auth. Login subset for unauth UI.
 */
export const LoginBrandingSchema = TenantBrandingSchema.pick({
  logoUrl: true,
  logoIconUrl: true,
  faviconUrl: true,
  loginBackgroundUrl: true,
  loginWelcomeText: true,
  loginSubtitle: true,
  portalTitle: true,
}).extend({
  primary: z.string().regex(HEX_COLOR_RE),
  background: z.string().regex(HEX_COLOR_RE),
});
export type LoginBranding = z.infer<typeof LoginBrandingSchema>;

// ─── System default — single source of truth ───────────────────────────

/**
 * SYSTEM_DEFAULT_BRANDING — the always-available fallback that even an
 * empty platform_branding row resolves to. Cannot be cleared further.
 *
 * The hex values mirror the OKLCH definitions in the web theme.
 */
export const SYSTEM_DEFAULT_BRANDING: TenantBranding = {
  useDefaultTheme: true,
  displayMode: 'icon-name',
  portalTitle: 'ArgusAI',
  logoUrl: '/ArgusLogo_500px-Wordmark-TM.png',
  logoIconUrl: '/ArgusLogo_500px-Logo.png',
  faviconUrl: '/favicon.png',
  loginBackgroundUrl: null,
  loginWelcomeText: null,
  loginSubtitle: null,
  footerText: null,
  light: {
    primary: '#8B5CF6',
    primaryForeground: '#ffffff',
    background: '#fafafa',
    card: '#ffffff',
    text: '#1a1a1d',
    popover: '#ffffff',
    sidebarBackground: '#f5f5f5',
    sidebarText: '#1a1a1d',
    sidebarActive: '#8B5CF6',
    sidebarActiveText: '#ffffff',
    sidebarHover: '#efe8fd',
  },
  dark: {
    primary: '#9d70fa',
    primaryForeground: '#ffffff',
    background: '#222222',
    card: '#2e2e32',
    text: '#ededed',
    popover: '#333337',
    sidebarBackground: '#1c1c1c',
    sidebarText: '#b0b2ba',
    sidebarActive: '#8B5CF6',
    sidebarActiveText: '#ffffff',
    sidebarHover: '#342b42',
  },
  effectiveVersion: 0,
  lockedFields: [],
  source: 'system_default',
};

/** Default value used to seed empty platform_branding/organization_branding rows. */
export const SYSTEM_DEFAULT_LIGHT_THEME = SYSTEM_DEFAULT_BRANDING.light;
export const SYSTEM_DEFAULT_DARK_THEME = SYSTEM_DEFAULT_BRANDING.dark;

/**
 * Names of the writable fields. Used by the lock-enforcement code in
 * W1c-06 to validate `locked_fields[]` entries.
 */
export const WRITABLE_BRANDING_FIELDS = [
  'displayMode',
  'portalTitle',
  'logoFullAssetId',
  'logoIconAssetId',
  'faviconAssetId',
  'loginBackgroundAssetId',
  'loginWelcomeText',
  'loginSubtitle',
  'footerText',
  'light.primary',
  'light.primaryForeground',
  'light.background',
  'light.card',
  'light.text',
  'light.popover',
  'light.sidebarBackground',
  'light.sidebarText',
  'light.sidebarActive',
  'light.sidebarActiveText',
  'light.sidebarHover',
  'dark.primary',
  'dark.primaryForeground',
  'dark.background',
  'dark.card',
  'dark.text',
  'dark.popover',
  'dark.sidebarBackground',
  'dark.sidebarText',
  'dark.sidebarActive',
  'dark.sidebarActiveText',
  'dark.sidebarHover',
] as const;
export type WritableBrandingField = (typeof WRITABLE_BRANDING_FIELDS)[number];
